import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react'

import './layout.css'

export interface TabItem<K extends string = string> {
  id: K
  /** 화면 표기. 호출부에서 t() 를 거쳐 넣는다 — 이 컴포넌트는 사전을 모른다 */
  label: string
  /**
   * 글자 «앞»의 작은 아이콘(장식 · `aria-hidden` 은 넘기는 쪽이 건다 — 이름은 글자가 말한다). 색은 탭 글자색을 따른다(`currentColor`).
   * 예: 웹훅 추가 창의 채널 로고(eric 2026-10-28). 안 넘기면 종전 그대로다.
   */
  icon?: ReactNode
  /** 탭 옆 카운트(`.cnt2` 435행). 0 이나 없으면 그리지 않는다 */
  count?: number
  /**
   * 글자 뒤에 붙는 표지(레시피 편집기의 「고칠 것 N」 · 2026-09-30). 안 연 탭의 문제가 숨지 않게 한다.
   * 🔴 표지는 **스스로 읽히는 글자**를 지녀야 한다(글리프 + 숫자 + 보조기술용 문구) — 색만으로 말하지 않는다.
   */
  mark?: ReactNode
  /**
   * 권한·상태가 막은 탭. 🔴 **숨기지 않는다** — 비활성으로 남기고 사유를 붙인다.
   * CLAUDE.md RBAC: 「권한 없는 버튼은 숨기지 않고 비활성 + 사유 표시. UI 숨김은 보안이 아니다」.
   * ⚠ 기본값은 활성이라 기존 호출부는 영향받지 않는다.
   */
  disabled?: boolean
  /** 왜 막혔는지. 화면에도(`title`) 스크린리더에도(`.a-sr`) 나간다 */
  disabledReason?: string
}

export interface TabsProps<K extends string = string> {
  items: readonly TabItem<K>[]
  value: K
  onChange: (id: K) => void
  /** 탭 줄 접근성 이름. 화면마다 탭이 여러 벌이라 필수다 */
  label: string
  /**
   * 탭 줄 오른쪽 끝 슬롯(`.tabact` 434행). 주 버튼이 들어간다.
   * 탭이 하나뿐이라 탭 줄이 사라지면 이 슬롯도 같이 사라진다 — 주 버튼은 검색 툴바로 옮겨라(§1-3).
   */
  actions?: ReactNode
  /**
   * `page` = 관리형 화면 밑줄 탭 · `fig` = 모니터링·패드 그림 탭(알약꼴) ·
   * `sub` = **상위 탭 줄 아래 오는 하위 줄**(로그의 이벤트·앱 로그). 위계가 보이게 한 단계 작다.
   * `segment` = **가로 전체를 탭 수로 등분**하는 큰 선택기(모니터링 [시료][장비] · eric 2026-09-22
   *   「오른쪽에 있으니 눈에 안 띄어 — 가로 너비 전체를 2분할로」). 알약(`fig`)은 그림 위에 얹는 작은 것이라 갈랐다.
   * `subnav` = **2단 탭** — 무리 탭(`page`) 아래 «어디로 갈지»(하위 메뉴 · 템플릿 종류 등). `page` 와 같은 밑줄 탭을 한 단 작게.
   *   🔴 검색 옆 칩(`sub` · «무엇을 걸러 볼지»)과 다르다 — 하위 메뉴를 검색 옆 칩으로 두지 않는다(eric 2026-10-13 「검색창 왼쪽에 이렇게
   *   놓은 건 보통 필터링할 때 목적으로 한 건데.. 지금은 필터링이 아니라 서브 메뉴잖아」). 자리는 `ListPage` 의 `subTabs`(툴바 줄 왼쪽 맨 앞)
   */
  variant?: 'page' | 'fig' | 'sub' | 'segment' | 'subnav'
}

/**
 * 가로 탭. `.atabs`(CSS 431행) / 모니터링 `.figtabs`(846 · 866–868행) 두 벌을 한 컴포넌트로 묶었다.
 *
 * 🔴 **탭이 하나뿐이면 탭 줄 자체를 두지 않는다**(eric 규칙 · 와이어프레임 2388행 · §1-3).
 *    이 컴포넌트가 그 규칙을 집행한다 — 호출부에서 조건부로 감싸지 마라.
 *
 * 와이어프레임의 탭은 `<div data-ptab>` 이라 키보드로 못 간다(§11-4-3). 여기서는
 * `role="tab"` + 좌우/Home/End 키 + roving tabindex 로 바로잡았다.
 */
export function Tabs<K extends string>({
  items,
  value,
  onChange,
  label,
  actions,
  variant = 'page',
}: TabsProps<K>) {
  const baseId = useId()
  const listRef = useRef<HTMLDivElement>(null)

  // 탭이 하나뿐이면 줄을 두지 않는다 — 주 버튼은 검색 툴바 오른쪽 끝으로 간다(§14-1)
  if (items.length < 2) return null

  // 비활성 탭은 건너뛴다 — 키보드로 갈 수 없는 자리에 포커스를 두면 길이 막힌다
  const move = (delta: number) => {
    const index = items.findIndex((item) => item.id === value)
    let next: TabItem<K> | undefined
    for (let step = 1; step <= items.length; step += 1) {
      const candidate = items[(index + delta * step + items.length * step) % items.length]
      if (candidate && candidate.disabled !== true) {
        next = candidate
        break
      }
    }
    if (!next) return
    onChange(next.id)
    // roving tabindex — 옮긴 탭으로 포커스도 같이 간다
    listRef.current
      ?.querySelector<HTMLButtonElement>(`#${CSS.escape(`${baseId}-${next.id}`)}`)
      ?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const handlers: Record<string, () => void> = {
      ArrowRight: () => move(1),
      ArrowLeft: () => move(-1),
      Home: () => {
        const first = items.find((item) => item.disabled !== true)
        if (first) onChange(first.id)
      },
      End: () => {
        const last = [...items].reverse().find((item) => item.disabled !== true)
        if (last) onChange(last.id)
      },
    }
    const handler = handlers[event.key]
    if (!handler) return
    event.preventDefault()
    handler()
  }

  return (
    <div className={`tabs tabs--${variant}`}>
      <div
        className="tabs__list"
        role="tablist"
        aria-label={label}
        ref={listRef}
        onKeyDown={onKeyDown}
      >
        {items.map((item) => {
          const selected = item.id === value
          const blocked = item.disabled === true
          return (
            <button
              key={item.id}
              id={`${baseId}-${item.id}`}
              type="button"
              role="tab"
              className={`tabs__tab${blocked ? ' tabs__tab--locked' : ''}${item.icon === undefined ? '' : ' tabs__tab--icon'}`}
              aria-selected={selected}
              // 🔴 `disabled` 속성을 쓰지 않는다 — 그러면 스크린리더가 탭 자체를 못 읽어
              //    「있는데 막혔다」가 전달되지 않는다. 사이드바 잠긴 항목과 같은 방식이다
              aria-disabled={blocked || undefined}
              tabIndex={selected ? 0 : -1}
              title={blocked ? item.disabledReason : undefined}
              onClick={() => {
                if (blocked) return
                onChange(item.id)
              }}
            >
              {item.icon ?? null}
              {item.label}
              {item.count ? <span className="tabs__count">{item.count}</span> : null}
              {item.mark ?? null}
              {blocked && item.disabledReason !== undefined ? (
                <span className="a-sr">{item.disabledReason}</span>
              ) : null}
            </button>
          )
        })}
      </div>
      {actions ? <div className="tabs__actions">{actions}</div> : null}
    </div>
  )
}
